import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  TextField,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  Stack,
  Fade,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  NavigateNext as NavigateNextIcon,
  Save as SaveIcon,
  PointOfSale as PointOfSaleIcon,
  ViewList as ViewListIcon,
  TouchApp as TouchAppIcon,
  Inventory as InventoryIcon,
  Keyboard as KeyboardIcon,
  SettingsApplications as SettingsIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import companySettingsService from '../../services/companySettingsService';
import { useSnackbar } from 'notistack';

const PAGE_KEY_POS_SALES_KEYBOARD = 'POS_SALES_KEYBOARD';

/** Data table column visibility for POS Sales Keyboard. */
const FIELD_VISIBILITY_OPTIONS = [
  { key: 'mrp', label: 'Show MRP column', icon: <CalculateIcon fontSize="small" /> },
  { key: 'gst', label: 'Show GST (%) column', icon: <CalculateIcon fontSize="small" /> },
  { key: 'uom', label: 'Show UOM column', icon: <ViewListIcon fontSize="small" /> },
  { key: 'discount', label: 'Show Discount column', icon: <CalculateIcon fontSize="small" /> },
  { key: 'showSaleRate', label: 'Show Sale Rate (Exclusive)', icon: <CalculateIcon fontSize="small" /> },
  { key: 'showSaleRateInclusive', label: 'Show Sale Rate (Inclusive of GST)', icon: <CalculateIcon fontSize="small" /> },
  { key: 'showItemHistory', label: 'Show Item History (Sale)', icon: <ViewListIcon fontSize="small" /> },
  { key: 'showPurchaseItemHistory', label: 'Show Item History (Purchase)', icon: <ViewListIcon fontSize="small" /> },
  { key: 'showPurchaseRate', label: 'Show Purchase Rate', icon: <CalculateIcon fontSize="small" /> },
];

/** Focus/tab order fields for POS Sales Keyboard. Order number (1, 2, 3, …) or empty to skip. */
const FOCUS_ORDER_FIELDS = [
  { key: 'itemCode', label: 'Item Code' },
  { key: 'qty', label: 'Qty' },
  { key: 'uom', label: 'UOM' },
  { key: 'saleRate', label: 'Sale Rate' },
  { key: 'saleRateInclusive', label: 'Sale Rate (Inclusive)' },
  { key: 'discount', label: 'Discount (% / ₹)' },
];

/** Initial focus selection for POS Sales Keyboard. */
const INITIAL_FOCUS_OPTIONS = [
  { key: 'CUSTOMER', label: 'Customer field' },
  { key: 'BARCODE', label: 'Barcode / Item Code' },
  { key: 'GRID', label: 'Grid (Item Code column)' },
];

const PageFieldSettingsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await companySettingsService.getPageFieldConfig(PAGE_KEY_POS_SALES_KEYBOARD);
      const safe = data && typeof data === 'object' ? data : {};
      if (safe.roundOffEnabled === undefined) {
        safe.roundOffEnabled = false;
      }
      if (!safe.roundOffMode) {
        safe.roundOffMode = 'NEAREST';
      }
      if (!safe.initialFocus) {
        safe.initialFocus = 'GRID';
      }
      if (safe.showItemHistory === undefined) {
        safe.showItemHistory = true;
      }
      if (safe.showPurchaseItemHistory === undefined) {
        safe.showPurchaseItemHistory = true;
      }
      if (safe.showSaleRate === undefined) {
        safe.showSaleRate = true;
      }
      if (safe.showSaleRateInclusive === undefined) {
        safe.showSaleRateInclusive = false;
      }
      if (safe.showPurchaseRate === undefined) {
        safe.showPurchaseRate = false;
      }
      if (safe.allowDecimalQty === undefined) {
        safe.allowDecimalQty = false;
      }
      setConfig(safe);
    } catch {
      setConfig({});
    } finally {
      setLoading(false);
    }
  };

  const getFieldValue = (fieldKey) => config[fieldKey] !== false;

  const setFieldValue = (fieldKey, value) => {
    setConfig((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const getFocusOrderValue = (fieldKey) => {
    const focusOrder = config?.focusOrder;
    if (!focusOrder || typeof focusOrder !== 'object') return '';
    const v = focusOrder[fieldKey];
    if (v == null || v === '') return '';
    const n = Number(v);
    return Number.isInteger(n) && n >= 1 ? String(n) : '';
  };

  const setFocusOrderValue = (fieldKey, value) => {
    setConfig((prev) => {
      const currentFocus = prev.focusOrder && typeof prev.focusOrder === 'object' ? { ...prev.focusOrder } : {};
      if (value === '' || value == null) {
        delete currentFocus[fieldKey];
      } else {
        const n = parseInt(value, 10);
        if (Number.isInteger(n) && n >= 1) currentFocus[fieldKey] = n;
      }
      return { ...prev, focusOrder: currentFocus };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companySettingsService.updatePageFieldConfig(PAGE_KEY_POS_SALES_KEYBOARD, config);
      enqueueSnackbar('POS settings saved successfully.', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to save POS settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/dashboard" color="inherit" underline="hover">
            Dashboard
          </Link>
          <Typography color="text.primary">POS Settings</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                mr: 2,
                display: 'flex',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
              }}
            >
              <PointOfSaleIcon fontSize="large" />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="700" color="text.primary">
                POS Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customize your Point of Sale experience
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.2,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              '&:hover': { boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)' }
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              p: 1
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
                '& .MuiTab-root': {
                  minHeight: 60,
                  fontWeight: '600',
                  fontSize: '1rem',
                  textTransform: 'none',
                  color: 'text.secondary',
                  borderRadius: 2,
                  mx: 0.5,
                  px: 3,
                  transition: 'all 0.2s',
                  '&.Mui-selected': {
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  },
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  }
                }
              }}
            >
              <Tab icon={<ViewListIcon sx={{ fontSize: 24 }} />} iconPosition="start" label="Display Fields" />
              <Tab icon={<KeyboardIcon sx={{ fontSize: 24 }} />} iconPosition="start" label="Input & Navigation" />
              <Tab icon={<CalculateIcon sx={{ fontSize: 24 }} />} iconPosition="start" label="Billing & Rounding" />
              <Tab icon={<InventoryIcon sx={{ fontSize: 24 }} />} iconPosition="start" label="Inventory Logic" />
            </Tabs>
          </Paper>

          <Box sx={{ flexGrow: 1 }}>
            {/* Tab 0: Display Fields */}
            {tabValue === 0 && (
              <Fade in={tabValue === 0}>
                <Box>
                  <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ViewListIcon color="primary" /> Column Visibility
                      </Typography>
                      <Grid container spacing={3}>
                        {FIELD_VISIBILITY_OPTIONS.map((field) => (
                          <Grid item xs={12} sm={6} key={field.key}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                transition: 'all 0.2s',
                                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.light' }
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={getFieldValue(field.key)}
                                    onChange={(e) => setFieldValue(field.key, e.target.checked)}
                                    color="primary"
                                  />
                                }
                                label={field.label}
                                sx={{ m: 0, width: '100%' }}
                              />
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              </Fade>
            )}

            {/* Tab 1: Input & Navigation */}
            {tabValue === 1 && (
              <Fade in={tabValue === 1}>
                <Box>
                  <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TouchAppIcon color="primary" /> Initial Cursor Position
                      </Typography>
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                          value={config.initialFocus || 'GRID'}
                          onChange={(e) => setFieldValue('initialFocus', e.target.value)}
                        >
                          <Grid container spacing={2}>
                            {INITIAL_FOCUS_OPTIONS.map((opt) => (
                              <Grid item xs={12} sm={4} key={opt.key}>
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    bgcolor: config.initialFocus === opt.key ? alpha('#1976d2', 0.03) : 'transparent',
                                    borderColor: config.initialFocus === opt.key ? 'primary.main' : 'divider',
                                    '&:hover': { borderColor: 'primary.main' }
                                  }}
                                  onClick={() => setFieldValue('initialFocus', opt.key)}
                                >
                                  <FormControlLabel
                                    value={opt.key}
                                    control={<Radio color="primary" />}
                                    label={opt.label}
                                    sx={{ m: 0, width: '100%' }}
                                  />
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </FormControl>
                    </CardContent>
                  </Card>

                  <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <KeyboardIcon color="primary" /> Keyboard Tab Order
                      </Typography>
                      <Grid container spacing={3}>
                        {FOCUS_ORDER_FIELDS.map((f) => (
                          <Grid item xs={6} sm={3} key={f.key}>
                            <TextField
                              size="medium"
                              label={f.label}
                              type="number"
                              value={getFocusOrderValue(f.key)}
                              onChange={(e) => setFocusOrderValue(f.key, e.target.value)}
                              placeholder="Skip"
                              fullWidth
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ min: 1, max: 10, step: 1 }}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>

                  <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mt: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SettingsIcon color="primary" /> Qty Input Mode
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(config.allowDecimalQty)}
                              onChange={(e) => setFieldValue('allowDecimalQty', e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Allow decimal values in Qty column (e.g. 1.5)"
                          sx={{ m: 0 }}
                        />
                      </Paper>
                    </CardContent>
                  </Card>
                </Box>
              </Fade>
            )}

            {/* Tab 2: Billing & Rounding */}
            {tabValue === 2 && (
              <Fade in={tabValue === 2}>
                <Box>
                  <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SettingsIcon color="primary" /> Pricing Preferences
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(config.autoApplyPreviousPrice)}
                              onChange={(e) => setFieldValue('autoApplyPreviousPrice', e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Auto-apply last sale price for customer"
                          sx={{ m: 0 }}
                        />
                      </Paper>
                    </CardContent>
                  </Card>

                  <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalculateIcon color="primary" /> Round Off Settings
                      </Typography>

                      <Box sx={{ mb: 3 }}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: config.roundOffEnabled ? alpha('#1976d2', 0.02) : 'transparent',
                            borderColor: config.roundOffEnabled ? 'primary.main' : 'divider',
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(config.roundOffEnabled)}
                                onChange={(e) => setFieldValue('roundOffEnabled', e.target.checked)}
                                color="primary"
                              />
                            }
                            label={<Typography fontWeight="600">Enable Round Off</Typography>}
                            sx={{ m: 0 }}
                          />
                        </Paper>
                      </Box>

                      {config.roundOffEnabled && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                            Select Rounding Logic
                          </Typography>
                          <RadioGroup
                            value={config.roundOffMode || 'NEAREST'}
                            onChange={(e) => setFieldValue('roundOffMode', e.target.value)}
                          >
                            <Grid container spacing={2}>
                              {[
                                { val: 'DOWN', label: 'Down', desc: '11.89 → 11' },
                                { val: 'UP', label: 'Up', desc: '11.01 → 12' },
                                { val: 'NEAREST', label: 'Nearest', desc: '(.49 ↓, .50 ↑)' }
                              ].map((item) => (
                                <Grid item xs={12} sm={4} key={item.val}>
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      cursor: 'pointer',
                                      borderWidth: config.roundOffMode === item.val ? 2 : 1,
                                      borderColor: config.roundOffMode === item.val ? 'primary.main' : 'divider',
                                      bgcolor: config.roundOffMode === item.val ? alpha('#1976d2', 0.03) : 'transparent',
                                      '&:hover': { borderColor: 'primary.main' }
                                    }}
                                    onClick={() => setFieldValue('roundOffMode', item.val)}
                                  >
                                    <FormControlLabel
                                      value={item.val}
                                      control={<Radio size="small" />}
                                      label={
                                        <Box>
                                          <Typography variant="body1" fontWeight="600">{item.label}</Typography>
                                          <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                                        </Box>
                                      }
                                      sx={{ m: 0, alignItems: 'flex-start' }}
                                    />
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </RadioGroup>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Fade>
            )}

            {/* Tab 3: Inventory Logic */}
            {tabValue === 3 && (
              <Fade in={tabValue === 3}>
                <Box>
                  <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InventoryIcon color="primary" /> Stock Control
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(config.allowNegativeStock)}
                              onChange={(e) => setFieldValue('allowNegativeStock', e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Allow sales when stock is zero or negative"
                          sx={{ m: 0 }}
                        />
                      </Paper>
                    </CardContent>
                  </Card>
                </Box>
              </Fade>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', display: { xs: 'flex', md: 'none' } }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: 2, py: 1.5 }}
          >
            {saving ? 'Saving...' : 'Save POS Settings'}
          </Button>
        </Box>
      </Container>
    </Layout>
  );
};

export default PageFieldSettingsPage;
